import * as React from 'react';
import { Button, Modal, Field } from '@teamsnap/teamsnap-ui';
import './divisions-review.scss';
import { SeasonGuideContext } from './season-guide';
export interface Division {
  name: string;
  competitionGenderName?: string;
  ageDivisionName?: string;
}

export const DivisionsReview = () => {
  const { season, setSeason } = React.useContext(SeasonGuideContext);
  const [newDivisionName, setNewDivisionName] = React.useState<string>('');
  const [editDivisionIndex, setEditDivisionIndex] = React.useState<number>(null);
  const [deleteDivisionIndex, setDeleteDivisionIndex] = React.useState<number>(null);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
  const { organizeDivisionsByAge, organizeMultipleDivisions, ageGroups } = season;

  React.useEffect(() => {
    if (organizeMultipleDivisions && organizeDivisionsByAge) {
      setSeason({
        divisions: ageGroups
          .map((ageGroup) =>
            ageGroup.genders.length > 0
              ? ageGroup.genders.map((gender) => ({
                  name: `${ageGroup.id} ${gender}`,
                  competitionGenderName: gender,
                  ageDivisionName: ageGroup.id,
                }))
              : { name: ageGroup.id, ageDivisionName: ageGroup.id }
          )
          .flat(),
      });
    } else {
      setSeason({ divisions: [] });
    }
  }, []);

  return (
    <div className="divisions-review">
      {(!organizeDivisionsByAge || season.divisions.length <= 0) && (
        <>
          <h2>Review your divisions. *</h2>
          <div className="FieldGroup-message FieldGroup-message">
            <p>
              There are no divisions created for this new season. You can choose to create your season without divisions
              and manually add divisions at a later time.
            </p>
            <br />
            <p>
              If you wish to automatically create divisions with this new season, return to the following page to define
              how your divisions should be structured.
            </p>
          </div>
        </>
      )}

      {organizeDivisionsByAge && season.divisions.length > 0 && (
        <>
          <h2>Review your {season.divisions.length} divisions. *</h2>
          {season.divisions.map((division, index) => (
            <div className="division-row" key={index}>
              <div className="division">
                <p>{division.name}</p>
              </div>
              <div className="division-actions">
                <Button
                  type="button"
                  onClick={() => {
                    setEditDivisionIndex(index);
                    setNewDivisionName(division.name);
                    setIsEditModalOpen(true);
                  }}
                  icon="edit"
                  color="negative"
                >
                  Edit Division Name
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setDeleteDivisionIndex(index);
                    setIsDeleteModalOpen(true);
                  }}
                  icon="trash"
                  color="negative"
                >
                  Delete Division
                </Button>
              </div>
            </div>
          ))}
          <Modal heading="" show={isEditModalOpen} closeFn={() => setIsEditModalOpen(false)}>
            <Field
              data-testid="division-name"
              type="text"
              name="divisionName"
              label="Division Name"
              formFieldProps={{
                size: 'large',
                placeholder: season.divisions[editDivisionIndex]?.name,
                inputProps: {
                  value: newDivisionName,
                  onChange: (e) => {
                    setNewDivisionName(e.target.value);
                  },
                },
              }}
            />
            <div className="btn-group u-spaceTopMd u-textRight">
              <Button type="link" mods="u-spaceRightSm u-colorGrey" onClick={() => setIsEditModalOpen(false)}>
                <span>Cancel</span>
              </Button>
              <Button
                type="link"
                onClick={() => {
                  season.divisions[editDivisionIndex].name = newDivisionName;
                  setSeason({ divisions: season.divisions });
                  setIsEditModalOpen(false);
                }}
              >
                <span>Save</span>
              </Button>
            </div>
          </Modal>
          <Modal heading="" show={isDeleteModalOpen} closeFn={() => setIsDeleteModalOpen(false)}>
            <div className="FieldGroup-message FieldGroup-message">
              <p>
                Are you sure you wish to delete this division? You will need to go back a couple steps to add new
                divisions.
              </p>
            </div>
            <div className="btn-group u-spaceTopMd u-textRight">
              <Button type="link" mods="u-spaceRightSm u-colorGrey" onClick={() => setIsDeleteModalOpen(false)}>
                <span>Cancel</span>
              </Button>
              <Button
                type="link"
                onClick={() => {
                  season.divisions.splice(deleteDivisionIndex, 1);
                  setSeason({ divisions: season.divisions });
                  setIsDeleteModalOpen(false);
                }}
              >
                <span>Delete</span>
              </Button>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default DivisionsReview;
